import { useSyncExternalStore } from 'react';
import { readToken } from '@/styles/tokens';

/**
 * Subscribes to a CSS media query. Re-renders only when the match flips.
 * Server/test environments without `matchMedia` report `false`.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    () =>
      typeof window !== 'undefined' && !!window.matchMedia
        ? window.matchMedia(query).matches
        : false,
    () => false,
  );
}

/** Tailwind's default breakpoints — the values `@theme` exposes as `--breakpoint-*`. */
const BREAKPOINT_DEFAULTS = {
  xs: '29.6875rem',
  sm: '40rem',
  md: '48rem',
  lg: '64rem',
  xl: '80rem',
  '2xl': '96rem',
} as const;

export type Breakpoint = keyof typeof BREAKPOINT_DEFAULTS;

/** Width of a theme breakpoint, read from `--breakpoint-<name>` so the theme stays the source. */
export function breakpointWidth(name: Breakpoint): string {
  return readToken(`--breakpoint-${name}`, BREAKPOINT_DEFAULTS[name]);
}

/** True at and above the given theme breakpoint — the JS twin of a `md:` prefix. */
export function useBreakpointUp(name: Breakpoint): boolean {
  return useMediaQuery(`(min-width: ${breakpointWidth(name)})`);
}
